import { Modal, Button, Form, Alert, Image } from 'react-bootstrap';
import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';

// Returns the highest-confidence flagged category from the moderation result,
// formatted as human-readable text (e.g. "HATE_SPEECH" → "hate speech")
// Bedrock returns confidence as a string ("HIGH", "MEDIUM", "LOW", "NONE")
const CONFIDENCE_RANK = { HIGH: 3, MEDIUM: 2, LOW: 1, NONE: 0 };

const getTopFlaggedCategory = (categories) => {
    if (!categories || Object.keys(categories).length === 0) return null;
    const entries = Object.entries(categories);
    const sorted = entries.sort((a, b) => {
        const aRank = CONFIDENCE_RANK[a[1]?.confidence] ?? 0;
        const bRank = CONFIDENCE_RANK[b[1]?.confidence] ?? 0;
        return bRank - aRank;
    });
    return sorted[0][0].replace(/[_-]/g, ' ').toLowerCase();
};

import {
    validateImageFile,
    ACCEPTED_FILE_EXTENSIONS,
} from '@/shared/utils/imageValidation';

const MAX_WORDS = 500;

const RESOURCE_TYPES = [
    { value: 'article', label: 'Article' },
    { value: 'dataset', label: 'Dataset' },
    { value: 'tool', label: 'Tool' },
    { value: 'report', label: 'Report' },
];

const isValidUrl = (value) => {
    try {
        const parsed = new URL(value);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
};

const CreatePostModal = ({
    show,
    onHide,
    onSubmit,
    onViewGuidelines = null,
    initialPostType = 'discussion',
    initialTitle = '',
    initialContent = '',
    initialResourceUrl = '',
    initialResourceType = '',
}) => {
    const [postType, setPostType] = useState(initialPostType || 'discussion');
    const [postTitle, setPostTitle] = useState(initialTitle || '');
    const [postContent, setPostContent] = useState(initialContent || '');
    const [resourceUrl, setResourceUrl] = useState(initialResourceUrl || '');
    const [resourceType, setResourceType] = useState(initialResourceType || '');
    const [eventDate, setEventDate] = useState('');
    const [eventEndDate, setEventEndDate] = useState('');
    const [eventLocation, setEventLocation] = useState('');
    const [eventRegistrationUrl, setEventRegistrationUrl] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [moderationAlert, setModerationAlert] = useState(null);
    const fileInputRef = useRef(null);

    const countWords = (text) => {
        return text
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0).length;
    };

    const wordCount = countWords(postContent);
    const wordsRemaining = MAX_WORDS - wordCount;

    const handlePostTypeChange = (type) => {
        setPostType(type);
        setErrors({});
        setModerationAlert(null);
        // Clear type-specific fields when switching
        if (type === 'resource') {
            setSelectedImage(null);
            setImagePreview(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            setEventDate('');
            setEventEndDate('');
            setEventLocation('');
            setEventRegistrationUrl('');
        } else if (type === 'event') {
            setResourceUrl('');
            setResourceType('');
        } else {
            setResourceUrl('');
            setResourceType('');
            setEventDate('');
            setEventEndDate('');
            setEventLocation('');
            setEventRegistrationUrl('');
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (postType === 'resource') {
            if (!resourceUrl.trim()) {
                newErrors.resourceUrl = 'A URL is required for resource posts.';
            } else if (!isValidUrl(resourceUrl.trim())) {
                newErrors.resourceUrl =
                    'Please enter a valid URL starting with http:// or https://';
            }
        } else if (postType === 'event') {
            if (!eventDate) {
                newErrors.eventDate = 'A date is required for event posts.';
            }
            if (
                eventEndDate &&
                eventDate &&
                new Date(eventEndDate) <= new Date(eventDate)
            ) {
                newErrors.eventEndDate =
                    'End date must be after the start date.';
            }
            if (
                eventRegistrationUrl.trim() &&
                !isValidUrl(eventRegistrationUrl.trim())
            ) {
                newErrors.eventRegistrationUrl =
                    'Please enter a valid URL starting with http:// or https://';
            }
            if (!postContent.trim()) {
                newErrors.content = 'Please add a description for your event.';
            }
            if (wordCount > MAX_WORDS) {
                newErrors.content = `Post exceeds ${MAX_WORDS} word limit.`;
            }
        } else {
            if (!postContent.trim() && !selectedImage) {
                newErrors.content =
                    'Please add some text or an image to your post.';
            } else if (!postContent.trim() && selectedImage) {
                newErrors.content = 'Please add a description for your post.';
            }
            if (wordCount > MAX_WORDS) {
                newErrors.content = `Post exceeds ${MAX_WORDS} word limit.`;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleContentChange = (e) => {
        setPostContent(e.target.value);
        if (errors.content) setErrors({ ...errors, content: null });
        if (moderationAlert) setModerationAlert(null);
    };

    const handleResourceUrlChange = (e) => {
        setResourceUrl(e.target.value);
        if (errors.resourceUrl) setErrors({ ...errors, resourceUrl: null });
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validationError = validateImageFile(file);
        if (validationError) {
            setErrors({ ...errors, image: validationError });
            return;
        }

        setErrors({ ...errors, image: null });
        setSelectedImage(file);

        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        setModerationAlert(null);

        try {
            let submitData;
            if (postType === 'resource') {
                submitData = {
                    postType: 'resource',
                    title: postTitle,
                    content: postContent,
                    resourceUrl: resourceUrl.trim(),
                    resourceType,
                };
            } else if (postType === 'event') {
                submitData = {
                    postType: 'event',
                    title: postTitle,
                    content: postContent,
                    eventDate,
                    eventEndDate: eventEndDate || undefined,
                    eventLocation,
                    eventRegistrationUrl:
                        eventRegistrationUrl.trim() || undefined,
                    image: selectedImage,
                };
            } else {
                submitData = {
                    postType: 'discussion',
                    title: postTitle,
                    content: postContent,
                    image: selectedImage,
                };
            }

            const result = await onSubmit(submitData);

            if (result?.moderationStatus === 'blocked') {
                const category = getTopFlaggedCategory(
                    result.moderationResult?.categories
                );
                setModerationAlert({ type: 'blocked', category });
            } else if (result?.moderationStatus === 'pending_review') {
                setModerationAlert({ type: 'pending_review' });
            } else {
                resetForm();
                onHide();
            }
        } catch {
            setErrors({
                ...errors,
                submit: 'Failed to create post. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setPostType('discussion');
        setPostTitle('');
        setPostContent('');
        setResourceUrl('');
        setResourceType('');
        setEventDate('');
        setEventEndDate('');
        setEventLocation('');
        setEventRegistrationUrl('');
        setSelectedImage(null);
        setImagePreview(null);
        setErrors({});
        setModerationAlert(null);
    };

    const handleClose = () => {
        resetForm();
        onHide();
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            size="lg"
            aria-labelledby="create-post-modal-title"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title
                    as="h2"
                    id="create-post-modal-title"
                    className="text-primary fw-bold fs-4"
                >
                    {moderationAlert?.type === 'pending_review'
                        ? 'Post Saved'
                        : 'Create a Post'}
                </Modal.Title>
            </Modal.Header>

            {moderationAlert?.type === 'pending_review' ? (
                <>
                    <Modal.Body
                        className="pt-3 text-center py-5"
                        role="status"
                        aria-live="polite"
                    >
                        <i
                            className="bi bi-hourglass-split fs-1 text-warning mb-3 d-block"
                            aria-hidden="true"
                        />
                        <h3 className="fs-5 fw-semibold mb-2">
                            Saved for review
                        </h3>
                        <p className="text-primary mb-0">
                            Thanks! A moderator will review your post and
                            publish it if it meets our community guidelines.
                        </p>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button
                            variant="primary"
                            onClick={handleClose}
                            className="px-4 rounded-pill"
                        >
                            Done
                        </Button>
                    </Modal.Footer>
                </>
            ) : (
                <Form noValidate onSubmit={handleSubmit}>
                    {/* noValidate: the component validates in handleSubmit and
                        renders its own error messages. Without it, the browser's
                        native HTML5 validation (required, type="url") fires
                        first and blocks submit, so the custom messages never
                        show. */}
                    <Modal.Body className="pt-3">
                        {/* Post Type Toggle */}
                        <div
                            className="d-flex gap-2 mb-4"
                            role="group"
                            aria-label="Post type"
                        >
                            <Button
                                type="button"
                                variant={
                                    postType === 'discussion'
                                        ? 'primary'
                                        : 'outline-primary'
                                }
                                className="rounded-pill px-3"
                                onClick={() =>
                                    handlePostTypeChange('discussion')
                                }
                                aria-pressed={postType === 'discussion'}
                            >
                                <i
                                    className="bi bi-chat-text me-2"
                                    aria-hidden="true"
                                />
                                Discussion
                            </Button>
                            <Button
                                type="button"
                                variant={
                                    postType === 'resource'
                                        ? 'primary'
                                        : 'outline-primary'
                                }
                                className="rounded-pill px-3"
                                onClick={() => handlePostTypeChange('resource')}
                                aria-pressed={postType === 'resource'}
                            >
                                <i
                                    className="bi bi-link-45deg me-2"
                                    aria-hidden="true"
                                />
                                Resource
                            </Button>
                            <Button
                                type="button"
                                variant={
                                    postType === 'event'
                                        ? 'primary'
                                        : 'outline-primary'
                                }
                                className="rounded-pill px-3"
                                onClick={() => handlePostTypeChange('event')}
                                aria-pressed={postType === 'event'}
                            >
                                <i
                                    className="bi bi-calendar-event me-2"
                                    aria-hidden="true"
                                />
                                Event
                            </Button>
                        </div>

                        {moderationAlert?.type === 'blocked' && (
                            <Alert
                                variant="danger"
                                className="mb-3"
                                role="alert"
                                aria-live="assertive"
                            >
                                <Alert.Heading className="h6 fw-semibold">
                                    Your post was flagged by our content filter
                                </Alert.Heading>
                                {moderationAlert.category && (
                                    <p className="mb-2">
                                        <strong>Category: </strong>
                                        {moderationAlert.category}
                                    </p>
                                )}
                                <p className="mb-2">
                                    Please edit your post to comply with our
                                    community standards and try again.
                                </p>
                                <Button
                                    variant="link"
                                    className="p-0 text-danger-emphasis fw-medium"
                                    onClick={onViewGuidelines}
                                    aria-label="View community guidelines"
                                >
                                    View community guidelines
                                </Button>
                            </Alert>
                        )}
                        {errors.submit && (
                            <Alert variant="danger" className="mb-3">
                                {errors.submit}
                            </Alert>
                        )}

                        {/* Post Title */}
                        <Form.Group className="mb-3">
                            <Form.Label className="text-primary fw-medium">
                                Title (optional)
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Give your post a title..."
                                value={postTitle}
                                onChange={(e) => setPostTitle(e.target.value)}
                                className="create-post-title"
                            />
                        </Form.Group>

                        {/* Resource-specific fields */}
                        {postType === 'resource' && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label
                                        htmlFor="resource-url"
                                        className="text-primary fw-medium"
                                    >
                                        Resource URL{' '}
                                        <span aria-hidden="true">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        id="resource-url"
                                        type="url"
                                        placeholder="https://example.com/resource"
                                        value={resourceUrl}
                                        onChange={handleResourceUrlChange}
                                        isInvalid={!!errors.resourceUrl}
                                        required
                                        aria-describedby={
                                            errors.resourceUrl
                                                ? 'resource-url-error'
                                                : undefined
                                        }
                                    />
                                    <Form.Control.Feedback
                                        type="invalid"
                                        id="resource-url-error"
                                    >
                                        {errors.resourceUrl}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label
                                        htmlFor="resource-type"
                                        className="text-primary fw-medium"
                                    >
                                        Resource Type (optional)
                                    </Form.Label>
                                    <Form.Select
                                        id="resource-type"
                                        value={resourceType}
                                        onChange={(e) =>
                                            setResourceType(e.target.value)
                                        }
                                    >
                                        <option value="">
                                            Select a type...
                                        </option>
                                        {RESOURCE_TYPES.map((rt) => (
                                            <option
                                                key={rt.value}
                                                value={rt.value}
                                            >
                                                {rt.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </>
                        )}

                        {/* Event-specific fields */}
                        {postType === 'event' && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label
                                        htmlFor="event-date"
                                        className="text-primary fw-medium"
                                    >
                                        Event Date{' '}
                                        <span aria-hidden="true">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        id="event-date"
                                        type="datetime-local"
                                        value={eventDate}
                                        onChange={(e) => {
                                            setEventDate(e.target.value);
                                            if (errors.eventDate) {
                                                setErrors({
                                                    ...errors,
                                                    eventDate: null,
                                                });
                                            }
                                        }}
                                        isInvalid={!!errors.eventDate}
                                        required
                                        aria-describedby={
                                            errors.eventDate
                                                ? 'event-date-error'
                                                : undefined
                                        }
                                    />
                                    <Form.Control.Feedback
                                        type="invalid"
                                        id="event-date-error"
                                    >
                                        {errors.eventDate}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label
                                        htmlFor="event-end-date"
                                        className="text-primary fw-medium"
                                    >
                                        End Date (optional)
                                    </Form.Label>
                                    <Form.Control
                                        id="event-end-date"
                                        type="datetime-local"
                                        value={eventEndDate}
                                        onChange={(e) => {
                                            setEventEndDate(e.target.value);
                                            if (errors.eventEndDate) {
                                                setErrors({
                                                    ...errors,
                                                    eventEndDate: null,
                                                });
                                            }
                                        }}
                                        isInvalid={!!errors.eventEndDate}
                                        aria-describedby={
                                            errors.eventEndDate
                                                ? 'event-end-date-error'
                                                : undefined
                                        }
                                    />
                                    <Form.Control.Feedback
                                        type="invalid"
                                        id="event-end-date-error"
                                    >
                                        {errors.eventEndDate}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label
                                        htmlFor="event-location"
                                        className="text-primary fw-medium"
                                    >
                                        Location (optional)
                                    </Form.Label>
                                    <Form.Control
                                        id="event-location"
                                        type="text"
                                        placeholder="e.g., Room 101, JSSB or Virtual (Zoom)"
                                        value={eventLocation}
                                        onChange={(e) =>
                                            setEventLocation(e.target.value)
                                        }
                                        maxLength={500}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label
                                        htmlFor="event-registration-url"
                                        className="text-primary fw-medium"
                                    >
                                        Registration URL (optional)
                                    </Form.Label>
                                    <Form.Control
                                        id="event-registration-url"
                                        type="url"
                                        placeholder="https://example.com/register"
                                        value={eventRegistrationUrl}
                                        onChange={(e) => {
                                            setEventRegistrationUrl(
                                                e.target.value
                                            );
                                            if (errors.eventRegistrationUrl) {
                                                setErrors({
                                                    ...errors,
                                                    eventRegistrationUrl: null,
                                                });
                                            }
                                        }}
                                        isInvalid={
                                            !!errors.eventRegistrationUrl
                                        }
                                        aria-describedby={
                                            errors.eventRegistrationUrl
                                                ? 'event-reg-url-error'
                                                : undefined
                                        }
                                    />
                                    <Form.Control.Feedback
                                        type="invalid"
                                        id="event-reg-url-error"
                                    >
                                        {errors.eventRegistrationUrl}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </>
                        )}

                        {/* Text Content */}
                        <Form.Group className="mb-3">
                            <Form.Label className="text-primary fw-medium">
                                {postType === 'resource'
                                    ? 'Description (optional)'
                                    : postType === 'event'
                                      ? 'Event Description'
                                      : 'What would you like to share?'}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={postType === 'resource' ? 3 : 6}
                                placeholder={
                                    postType === 'resource'
                                        ? 'Add a description or context for this resource...'
                                        : 'Share your thoughts, ideas, or questions with the community...'
                                }
                                value={postContent}
                                onChange={handleContentChange}
                                isInvalid={!!errors.content}
                                className="create-post-textarea"
                            />
                            <div className="d-flex justify-content-between mt-2">
                                <Form.Control.Feedback type="invalid">
                                    {errors.content}
                                </Form.Control.Feedback>
                                <small
                                    className={`ms-auto ${wordsRemaining < 0 ? 'text-danger' : 'text-muted'}`}
                                >
                                    {wordsRemaining} words remaining
                                </small>
                            </div>
                        </Form.Group>

                        {/* Image Upload — discussion and event posts */}
                        {postType !== 'resource' && (
                            <Form.Group className="mb-3">
                                <Form.Label className="text-primary fw-medium">
                                    Add an Image (optional)
                                </Form.Label>
                                <div className="create-post-image-upload">
                                    {imagePreview ? (
                                        <div className="image-preview-container position-relative">
                                            <Image
                                                src={imagePreview}
                                                alt="Preview"
                                                fluid
                                                className="image-preview rounded"
                                            />
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                className="position-absolute top-0 end-0 m-2 rounded-circle"
                                                onClick={handleRemoveImage}
                                                aria-label="Remove image"
                                            >
                                                <i className="bi bi-x" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <div
                                            className="image-upload-zone d-flex flex-column align-items-center justify-content-center p-4 border rounded"
                                            onClick={() =>
                                                fileInputRef.current?.click()
                                            }
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key === 'Enter' ||
                                                    e.key === ' '
                                                ) {
                                                    fileInputRef.current?.click();
                                                }
                                            }}
                                        >
                                            <i className="bi bi-image fs-1 text-muted mb-2" />
                                            <p className="text-muted mb-1">
                                                Click to upload an image
                                            </p>
                                            <small className="text-muted">
                                                JPEG, PNG, or HEIC (max 5MB)
                                            </small>
                                        </div>
                                    )}
                                    <Form.Control
                                        type="file"
                                        ref={fileInputRef}
                                        accept={ACCEPTED_FILE_EXTENSIONS}
                                        onChange={handleImageSelect}
                                        className="d-none"
                                        aria-label="Upload an image"
                                    />
                                    {errors.image && (
                                        <div className="text-danger small mt-2">
                                            {errors.image}
                                        </div>
                                    )}
                                </div>
                            </Form.Group>
                        )}
                    </Modal.Body>

                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button
                            variant="outline-primary"
                            onClick={handleClose}
                            className="px-4 rounded-pill"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={isSubmitting || wordsRemaining < 0}
                            className="px-4 rounded-pill"
                        >
                            {isSubmitting ? 'Posting...' : 'Post'}
                        </Button>
                    </Modal.Footer>
                </Form>
            )}
        </Modal>
    );
};

CreatePostModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onViewGuidelines: PropTypes.func,
    initialPostType: PropTypes.oneOf(['discussion', 'resource', 'event']),
    initialTitle: PropTypes.string,
    initialContent: PropTypes.string,
    initialResourceUrl: PropTypes.string,
    initialResourceType: PropTypes.string,
};

export default CreatePostModal;
